import React, { useState } from "react";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import { copyJsonObject } from "../../util/commonUtils.js";
import { FREEFORM_REASON_CHAR_LIMIT_FOR_BID } from "../../util/constants";
import {
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  MANAGE_SHIFT_BID_INELIGIBLE_DIALOG_MESSAGE,
} from "../../util/messageConstants";
import { isPendingBid } from "../../util/bid/bidCommon.js";
import { BID_TYPE } from "../../util/bid/bidConstants";

const EmployeeEligibilityDialog = (props) => {
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentsErrorMessage, setCommentsErrorMessage] = useState("");

  const handleUpdateIneligibility = () => {
    if (comments === "") {
      setCommentsError(true);
      setCommentsErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > FREEFORM_REASON_CHAR_LIMIT_FOR_BID) {
      setCommentsError(true);
      setCommentsErrorMessage(CHAR_LIMIT_MESSAGE(FREEFORM_REASON_CHAR_LIMIT_FOR_BID));
    } else {
      // Update the checkbox
      const copiedBidGroup = copyJsonObject(props.bidGroup);
      const selectedEmployee = copiedBidGroup[props.employeeIdx];
      selectedEmployee.isEligible = !selectedEmployee.isEligible;
      if (isPendingBid(props.bidStatus)) {
        selectedEmployee.isEligibleAtOpen = selectedEmployee.isEligible;
      }
      const commentsArray = [
        {
          comments: comments,
          createdAt: DateTime.now().toMillis().toString(),
          createdBy: `${props.userSession.lastName}, ${props.userSession.firstName}`,
          isEligible: selectedEmployee.isEligible,
          rank: selectedEmployee.rank,
        },
        ...(Array.isArray(selectedEmployee?.comments) ? selectedEmployee.comments : []),
      ];

      selectedEmployee.comments = commentsArray;
      props.setBidGroup(copiedBidGroup);

      // remove assignedEmployee from bid lines if it is shift bid.
      if (props.bidType === BID_TYPE.SHIFT) {
        if (!selectedEmployee.isEligible) {
          const copiedBidLines = [...props.bidLines];
          const clickedBidlineIdx = copiedBidLines.findIndex(
            (bidline) => bidline?.assignedEmployee?.eid === selectedEmployee?.eid
          );
          if (clickedBidlineIdx !== -1) {
            copiedBidLines[clickedBidlineIdx].assignedEmployee = null;
            props.setBidLines(copiedBidLines);
          }
        }
      }
      props.onClickDialogClose();
      setCommentsError(false);
      setCommentsErrorMessage("");
      setComments("");
    }
  };

  return (
    <ConfirmationDialog
      isDialogOpen={props.isDialogOpen}
      title={
        props.bidGroup[props.employeeIdx]?.isEligible ? "Mark Employee as Ineligible" : "Mark Employee as Eligible"
      }
      message={MANAGE_SHIFT_BID_INELIGIBLE_DIALOG_MESSAGE(
        props.bidGroup[props.employeeIdx]?.lastName,
        props.bidGroup[props.employeeIdx]?.shortName,
        props.bidGroup[props.employeeIdx]?.isEligible ? "ineligible" : "eligible"
      )}
      hasComments={true}
      commentCharLimit={FREEFORM_REASON_CHAR_LIMIT_FOR_BID}
      comments={comments}
      commentsError={commentsError}
      commentErrorMessage={commentsErrorMessage}
      setComments={setComments}
      setCommentsError={setCommentsError}
      setCommentErrorMessage={setCommentsErrorMessage}
      handleNoButtonClicked={() => {
        props.onClickDialogClose();
        setCommentsError(false);
        setCommentsErrorMessage("");
        setComments("");
      }}
      handleYesButtonClicked={handleUpdateIneligibility}
      yesTitle={props.bidGroup[props.employeeIdx]?.isEligible ? "MARK AS INELIGIBLE" : "MARK AS ELIGIBLE"}
      noTitle="CANCEL"
    />
  );
};

EmployeeEligibilityDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  bidStatus: PropTypes.string.isRequired,
  bidGroup: PropTypes.array.isRequired,
  bidLines: PropTypes.array,
  setBidGroup: PropTypes.func.isRequired,
  setBidLines: PropTypes.func,
  employeeIdx: PropTypes.number,
  userSession: PropTypes.object.isRequired,
  bidType: PropTypes.string.isRequired,
};

export default EmployeeEligibilityDialog;
